import React, { useEffect, useState } from 'react';
import {
    ArrowDownward as ArrowDownwardIcon,
    ArrowUpward as ArrowUpwardIcon,
    Close as CloseIcon,
} from '@mui/icons-material';
import {
    Button,
    CircularProgress,
    Dialog,
    DialogContent,
    DialogTitle,
    IconButton,
    Typography,
} from '@mui/material';
import Box from '@mui/material/Box';
import { ProcessingBuildListItem, ToastType, Zone, ZoneListItem } from '../interfaces';
import TextField from '@mui/material/TextField';
import Autocomplete from '@mui/material/Autocomplete';
import { useAppSelector } from '../store/hooks';
import { selectProcessingBuilds, selectTileSourceOptions, selectZones } from '../store/maps.reducer';
import { MapPolygonPreviewer } from './MapPolygonPreviewer';
import { createProcessingBuild } from '../api/createProcessingBuild';
import { useToastMessage } from '../hooks/useToastMessage';
import ToastWrapper from './ToastWrapper';
import { Formik, Form, Field } from 'formik';
import { object, string, addMethod } from 'yup';
import { getZoneById } from '../api/getZoneById';
import { DateTime } from 'luxon';
import { DEFAULT_TILE_SOURCE_OPTIONS } from '../constants/tileSourceOptions';

declare module 'yup' {
    interface StringSchema {
        uniqueName(builds: ProcessingBuildListItem[], message: string): this;
    }
}

// Extend Yup for unique name validation
addMethod(string, 'uniqueName', function (processingBuilds: ProcessingBuildListItem[], message) {
    return this.test('uniqueName', message, function (value) {
        const { path, createError } = this;
        const isUnique = processingBuilds.every((build) => build.name.toLowerCase() !== value?.toLowerCase());
        return isUnique || createError({ path, message });
    });
});

interface Props {
    open: boolean;
    onClose: () => void;
    onProcessingBuildCreated: () => void;
}

export const AddProcessingBuildDialog = ({ open, onClose, onProcessingBuildCreated }: Props) => {
    const [selectedZone, setSelectedZone] = useState<ZoneListItem | null>(null);
    const [selectedZoneFull, setSelectedZoneFull] = useState<Zone | null>(null);
    const zones = useAppSelector(selectZones);
    const processingBuilds = useAppSelector(selectProcessingBuilds);
    const tileSourceOptions = useAppSelector(selectTileSourceOptions);
    const [loading, setLoading] = useState<boolean>(false);
    const { isOpened, toastMessage, setToastMessage, handleCloseToast } = useToastMessage();

    const [loadingMap, setLoadingMap] = useState(false);
    const availableTileSourceOptions =
        tileSourceOptions.length > 0 ? tileSourceOptions : DEFAULT_TILE_SOURCE_OPTIONS;
    const [tileSourcePriorities, setTileSourcePriorities] = useState<string[]>(availableTileSourceOptions);

    useEffect(() => {
        if (open) {
            setTileSourcePriorities(availableTileSourceOptions);
        }
    }, [open]);

    useEffect(() => {
        (async () => {
            if (selectedZone) {
                setLoadingMap(true);
                try {
                    const zone = await getZoneById(selectedZone._id);
                    setSelectedZoneFull(zone);
                } catch (error) {
                    console.error('Failed to fetch entity:', error);
                } finally {
                    setLoadingMap(false);
                }
            } else {
                setSelectedZoneFull(null);
            }
        })();
    }, [selectedZone]);

    const validationSchema = object({
        name: string()
            .required('Назва білда є обов’язковою')
            .matches(
                /^[a-zA-Z0-9-_]+$/,
                'Назва може містити лише латинські літери, цифри, дефіс (-) та підкреслення (_)',
            )
            .uniqueName(processingBuilds!, 'Назва білда повинна бути унікальною'),
        zoom: string().required('Зум є обов’язковим'),
    });

    const movePriority = (index: number, direction: -1 | 1) => {
        const targetIndex = index + direction;
        if (targetIndex < 0 || targetIndex >= tileSourcePriorities.length) {
            return;
        }

        setTileSourcePriorities((prev) => {
            const result = [...prev];
            [result[index], result[targetIndex]] = [result[targetIndex], result[index]];
            return result;
        });
    };

    const triggerCreateProcessingBuild = async (values: { name: string; zoom: string }) => {
        try {
            setLoading(true);
            await createProcessingBuild({
                name: values.name,
                zoneId: selectedZone!._id,
                zoom: parseInt(values.zoom, 10),
                tileSourcePriorities,
            });
            onProcessingBuildCreated();
        } catch (e) {
            console.error('Create processing build failed', e);
            setToastMessage({
                message: `При створенні білда відбулася помилка`,
                type: ToastType.WARNING,
            });
        } finally {
            setLoading(false);
        }
    };

    return (
        <Dialog fullScreen onClose={onClose} open={open}>
            <DialogTitle
                sx={{
                    display: 'flex',
                    justifyContent: 'right',
                }}
            >
                <CloseIcon sx={{ cursor: 'pointer' }} onClick={onClose} />
            </DialogTitle>
            <DialogContent>
                <Formik
                    initialValues={{
                        name: '',
                        zoom: '15',
                    }}
                    validationSchema={validationSchema}
                    onSubmit={triggerCreateProcessingBuild}
                >
                    {({ touched, errors, isValid, setFieldValue }) => (
                        <Form>
                            <Box sx={{ display: 'flex' }}>
                                <div className="flex-grow relative w-[340px] pt-2">
                                    <Box>
                                        {zones && (
                                            <Autocomplete
                                                onChange={(e, newValue) => {
                                                    if (!newValue) {
                                                        setSelectedZone(null);
                                                        setFieldValue('name', '');
                                                    } else {
                                                        const zone = zones.find((z) => z._id === newValue.id);
                                                        setSelectedZone(zone!);
                                                        setFieldValue(
                                                            'name',
                                                            `${zone!.name}-${DateTime.fromISO(
                                                                new Date().toISOString(),
                                                                { zone: 'utc' },
                                                            )
                                                                .setZone('Europe/Kyiv')
                                                                .toFormat('yyyyMMddHHmmss')}`,
                                                        );
                                                    }
                                                }}
                                                fullWidth
                                                size={'small'}
                                                renderInput={(params) => (
                                                    <TextField {...params} label="Зона" />
                                                )}
                                                isOptionEqualToValue={(newValue, oldValue) =>
                                                    newValue.id === oldValue.id
                                                }
                                                options={zones.map((zone) => ({
                                                    label: zone.name,
                                                    id: zone._id,
                                                }))}
                                            />
                                        )}
                                    </Box>

                                    <Box sx={{ marginTop: 2 }}>
                                        <Field
                                            as={TextField}
                                            name="name"
                                            label="Назва нового білда"
                                            fullWidth
                                            size="small"
                                            variant="outlined"
                                            error={touched.name && !!errors.name}
                                            helperText={touched.name && errors.name}
                                        />
                                    </Box>

                                    <Box sx={{ marginTop: 2 }}>
                                        <Field
                                            as={TextField}
                                            name="zoom"
                                            label="Зум"
                                            fullWidth
                                            size="small"
                                            variant="outlined"
                                            error={touched.zoom && !!errors.zoom}
                                            helperText={touched.zoom && errors.zoom}
                                        />
                                    </Box>

                                    <Box sx={{ marginTop: 2 }}>
                                        <Typography variant="subtitle2">
                                            Пріоритет джерел тайлів (згори вище)
                                        </Typography>
                                        <Box
                                            sx={{
                                                marginTop: 1,
                                                border: '1px solid #d0d7de',
                                                borderRadius: 1,
                                                padding: 1,
                                            }}
                                        >
                                            {tileSourcePriorities.map((source, index) => (
                                                <Box
                                                    key={source}
                                                    sx={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'space-between',
                                                        minHeight: 34,
                                                    }}
                                                >
                                                    <Typography variant="body2">
                                                        {index + 1}. {source}
                                                    </Typography>
                                                    <Box>
                                                        <IconButton
                                                            size="small"
                                                            onClick={() => movePriority(index, -1)}
                                                            disabled={index === 0}
                                                        >
                                                            <ArrowUpwardIcon fontSize="small" />
                                                        </IconButton>
                                                        <IconButton
                                                            size="small"
                                                            onClick={() => movePriority(index, 1)}
                                                            disabled={
                                                                index === tileSourcePriorities.length - 1
                                                            }
                                                        >
                                                            <ArrowDownwardIcon fontSize="small" />
                                                        </IconButton>
                                                    </Box>
                                                </Box>
                                            ))}
                                        </Box>
                                        <Button
                                            size="small"
                                            sx={{ marginTop: 1 }}
                                            onClick={() => {
                                                setTileSourcePriorities(availableTileSourceOptions);
                                            }}
                                        >
                                            Скинути порядок
                                        </Button>
                                    </Box>

                                    <Box sx={{ marginTop: 2 }}>
                                        <Button
                                            type="submit"
                                            fullWidth
                                            variant="outlined"
                                            disabled={
                                                !isValid ||
                                                !selectedZone ||
                                                loading ||
                                                tileSourcePriorities.length === 0
                                            }
                                        >
                                            Запустити білд
                                            {loading && (
                                                <span className="ml-2 inline-flex">
                                                    <CircularProgress size={15} />
                                                </span>
                                            )}
                                        </Button>
                                    </Box>
                                </div>
                                <Box
                                    sx={{
                                        marginLeft: 3,
                                        height: 'calc(100vh - 80px)',
                                        width: '100%',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                    }}
                                >
                                    {loadingMap ? (
                                        <CircularProgress />
                                    ) : selectedZoneFull ? (
                                        <MapPolygonPreviewer
                                            key={selectedZoneFull._id}
                                            geoJson={selectedZoneFull.geoJson}
                                        />
                                    ) : (
                                        <Box>No data available</Box>
                                    )}
                                </Box>
                            </Box>
                        </Form>
                    )}
                </Formik>
            </DialogContent>
            <ToastWrapper
                open={isOpened}
                anchorOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                }}
                autoHideDuration={3000}
                onClose={handleCloseToast}
                message={toastMessage?.message}
                type={toastMessage?.type}
            />
        </Dialog>
    );
};
